import React, { useState } from 'react';

/**
 * LeaderboardTab Component
 * 
 * Displays participant rankings with scores
 * Features:
 * - Top 10 performers highlighted
 * - Full list with pagination
 * - Sorting options (score, name, time)
 * - Visual rank indicators (gold, silver, bronze medals)
 */
function LeaderboardTab({ scores, instance }) {
  const [sortBy, setSortBy] = useState('score'); // 'score', 'name', 'time'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Sort scores
  const sortedScores = [...scores].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'time':
        return a.joinedAt - b.joinedAt;
      case 'score':
      default:
        if (b.score !== a.score) {
          return parseFloat(b.score) - parseFloat(a.score);
        }
        return a.joinedAt - b.joinedAt;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedScores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedScores = sortedScores.slice(startIndex, endIndex);

  // Get medal for top 3
  const getMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  // Get rank color
  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-600 font-bold';
    if (rank === 2) return 'text-gray-600 font-bold';
    if (rank === 3) return 'text-amber-700 font-bold';
    return 'text-gray-500';
  };

  // Get score color (for accuracy percentage, not points)
  const getScoreColor = (accuracy) => {
    const numScore = parseFloat(accuracy);
    if (numScore >= 90) return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/50';
    if (numScore >= 70) return 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/50';
    if (numScore >= 50) return 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/50';
    return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/50';
  };

  // Format score display - use isGamification flag from participant data
  const formatScore = (participant) => {
    if (participant.isGamification) {
      return `${Math.round(parseFloat(participant.score))} Pkt.`;
    }
    return `${participant.score}%`;
  };

  if (scores.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Noch keine Teilnehmer</h3>
        <p className="text-gray-500 dark:text-gray-400">Teilnehmer erscheinen hier, sobald sie dem Quiz beitreten.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400 font-medium">Sortieren nach:</label>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="score">Punkte (Höchste zuerst)</option>
            <option value="name">Name (A-Z)</option>
            <option value="time">Beitrittszeit (Erste zuerst)</option>
          </select>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Zeige {startIndex + 1}-{Math.min(endIndex, scores.length)} von {scores.length} Teilnehmern
        </div>
      </div>

      {/* Top 3 Podium (if on first page and sorted by score) */}
      {sortBy === 'score' && currentPage === 1 && scores.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* 2nd Place */}
          <div className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg p-6 text-center transform translate-y-4">
            <div className="text-4xl mb-2">🥈</div>
            <div className="text-lg font-semibold text-gray-800">{scores[1].name}</div>
            <div className="text-2xl font-bold text-gray-700 mt-2">{formatScore(scores[1])}</div>
            <div className="text-sm text-gray-600 mt-1">
              {scores[1].correctCount}/{scores[1].totalQuestions} richtig
            </div>
          </div>

          {/* 1st Place */}
          <div className="bg-gradient-to-b from-yellow-100 to-yellow-200 rounded-lg p-6 text-center">
            <div className="text-5xl mb-2">🥇</div>
            <div className="text-xl font-bold text-yellow-900">{scores[0].name}</div>
            <div className="text-3xl font-bold text-yellow-800 mt-2">{formatScore(scores[0])}</div>
            <div className="text-sm text-yellow-700 mt-1">
              {scores[0].correctCount}/{scores[0].totalQuestions} richtig
            </div>
            <div className="mt-3 inline-block px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-semibold">
              SIEGER
            </div>
          </div>

          {/* 3rd Place */}
          <div className="bg-gradient-to-b from-amber-100 to-amber-200 rounded-lg p-6 text-center transform translate-y-4">
            <div className="text-4xl mb-2">🥉</div>
            <div className="text-lg font-semibold text-amber-800">{scores[2].name}</div>
            <div className="text-2xl font-bold text-amber-700 mt-2">{formatScore(scores[2])}</div>
            <div className="text-sm text-amber-600 mt-1">
              {scores[2].correctCount}/{scores[2].totalQuestions} richtig
            </div>
          </div>
        </div>
      )}

      {/* Full Leaderboard Table */}
      <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                Rang
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Punkte
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Richtig
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Beantwortet
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedScores.map((participant, index) => {
              const rank = startIndex + index + 1;
              const medal = getMedal(rank);
              const isTopTen = rank <= 10;

              return (
                <tr
                  key={participant.participantId}
                  className={`${isTopTen ? 'bg-primary-50/30 dark:bg-primary-900/20' : ''} hover:bg-gray-50 dark:hover:bg-gray-800 theme-transition`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {medal ? (
                        <span className="text-2xl">{medal}</span>
                      ) : (
                        <span className={`text-sm font-medium ${getRankColor(rank)}`}>
                          #{rank}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold">
                        {participant.name[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{participant.name}</div>
                        {isTopTen && rank <= 3 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">Top-Leistung</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(participant.accuracy)}`}>
                      {formatScore(participant)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {participant.correctCount} / {participant.totalQuestions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {participant.totalAnswered} / {participant.totalQuestions}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Zurück
          </button>
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  page === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Weiter
          </button>
        </div>
      )}
    </div>
  );
}

export default LeaderboardTab;
